import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Calculator, BarChart2, Building2, MapPin, Menu, X, 
  Settings, LogIn, LogOut, User, Book, Shield 
} from 'lucide-react';
import { CURRENCIES } from '../utils/currencyUtils';
import { SettingsPopup } from './settings/SettingsPopup';
import { LoginModal } from './auth/LoginModal';
import { useAuthContext } from './auth/AuthProvider';

interface NavbarProps {
  currency: string;
  powerCost: number;
  onCurrencyChange: (currency: string) => void;
  onPowerCostChange: (cost: number) => void;
}

export function Navbar({ 
  currency, 
  powerCost, 
  onCurrencyChange, 
  onPowerCostChange 
}: NavbarProps) {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const { user, profile, signOut } = useAuthContext();
  
  const isActive = (path: string) => location.pathname === path;

  // Base navigation items
  const baseNavItems = [
    { path: '/', label: 'Mining Calculator', icon: Calculator },
    { path: '/markets', label: 'Markets', icon: BarChart2 },
    { path: '/exchanges', label: 'Exchanges', icon: Building2 },
    { path: '/facilities', label: 'Hosting', icon: MapPin },
    { path: '/resources', label: 'Resources', icon: Book }
  ];

  // Add admin dashboard for admin users
  const navItems = [...baseNavItems];
  if (profile?.role === 'admin') {
    navItems.push({ 
      path: '/admin', 
      label: 'Admin', 
      icon: Shield 
    });
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await signOut();
      setIsMobileMenuOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 glass-nav ${
      isScrolled ? 'shadow-lg' : ''
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center flex-shrink-0 group"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-2 transition-transform group-hover:scale-110 duration-300">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <div className="ml-3 flex flex-col">
              <span className="text-xl font-bold bg-gradient-to-r from-blue-700 to-purple-700 bg-clip-text text-transparent">
                CryptoCalc
              </span>
              <span className="text-xs font-medium text-gray-700">Mining Calculator</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden sm:flex sm:items-center sm:gap-8">
            {/* Navigation Links */}
            <div className="flex items-center space-x-8">
              {navItems.map(({ path, label, icon: Icon }) => (
                <Link
                  key={path}
                  to={path}
                  className={`glass-nav-item flex items-center gap-2 px-4 py-2 text-sm font-semibold ${
                    isActive(path) 
                      ? 'text-blue-700 bg-blue-50/90' 
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </Link>
              ))}
            </div>

            {/* Auth & Settings Buttons */}
            <div className="flex items-center gap-4 ml-auto">
              {user ? (
                <div className="flex items-center gap-4">
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <User className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      {profile?.display_name || user.email}
                    </span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors ml-auto"
                >
                  <LogIn className="w-4 h-4" />
                  Login
                </button>
              )}

              <button
                onClick={() => setIsSettingsOpen(true)}
                className="glass-button flex items-center gap-2 bg-white/90 hover:bg-white/95 text-gray-800 font-medium px-4 py-2 rounded-xl"
              >
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="glass-button p-2 rounded-xl"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Settings Popup */}
      <SettingsPopup
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        currency={currency}
        powerCost={powerCost}
        onCurrencyChange={onCurrencyChange}
        onPowerCostChange={onPowerCostChange}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />

      {/* Mobile Menu Backdrop */}
      <div 
        className={`fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        } sm:hidden`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div 
        className={`fixed inset-x-4 top-20 z-50 transform transition-all duration-300 ease-in-out ${
          isMobileMenuOpen 
            ? 'translate-y-0 opacity-100 visible'
            : '-translate-y-2 opacity-0 invisible pointer-events-none'
        } sm:hidden`}
      >
        <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {navItems.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                className={`
                  flex items-center gap-3 px-6 py-4 text-base font-medium transition-colors
                  ${isActive(path) 
                    ? 'text-blue-700 bg-blue-50/90' 
                    : 'text-gray-700 hover:bg-gray-50/90 active:bg-gray-100/90'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                {label}
              </Link>
            ))}
          </div>

          {/* Mobile Auth & Settings */}
          <div className="p-6 bg-gray-50/80">
            {user ? (
              <div className="space-y-3">
                <Link
                  to="/settings"
                  className="flex items-center gap-2 px-4 py-3 bg-white rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <User className="w-5 h-5 text-gray-600" />
                  <span className="font-medium text-gray-900">
                    {profile?.display_name || user.email}
                  </span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 bg-white rounded-xl hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setIsLoginModalOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors"
              >
                <LogIn className="w-5 h-5" />
                <span className="font-medium">Login</span>
              </button>
            )}

            <button
              onClick={() => {
                setIsSettingsOpen(true);
                setIsMobileMenuOpen(false);
              }}
              className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-3 text-gray-700 bg-white rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Settings className="w-5 h-5" />
              <span className="font-medium">Settings</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}